import React from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Link,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { DataGridPremium } from '@mui/x-data-grid-premium';
import { GRID_DETAIL_PANEL_TOGGLE_FIELD } from '@mui/x-data-grid-premium';
import { capitalize } from 'lodash';
import { formatInTimeZone } from 'date-fns-tz';
import { parseJSON, isAfter, subWeeks } from 'date-fns';
import ReactJson from 'react-json-view';
import {
  IconArrowsVertical,
  IconCodeDots,
  IconPlayerPlay,
  IconSearch,
  IconX,
} from '@tabler/icons';
import { useLogQuery } from '../../../data/useLogQuery';
import LogQueryToolbar from '../../logs/LogQueryToolbar';
import { buildBasicQuery } from '../../logs/buildBasicQuery';
import AQDatePicker from '../../AQDatePicker';
import SurroundingLogsDialog from './SurroundingLogsDialog';

function CustomNoRowsOverlay({ sync }: { sync: any }) {
  const isOverTwoWeeksOld = React.useMemo(() => {
    if (!sync) return false;
    const twoWeeksAgo = subWeeks(new Date(), 2);
    if (sync.started_at) {
      const startedAt = parseJSON(sync.started_at);
      if (isAfter(twoWeeksAgo, startedAt)) return true;
    }
    if (sync.run_at) {
      const runAt = parseJSON(sync.run_at);
      if (isAfter(twoWeeksAgo, runAt)) return true;
    }
    return false;
  }, [sync]);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
      }}
    >
      <Typography variant="h6" color="textSecondary">
        No logs available
        {['queued', 'running', undefined].includes(sync?.status)
          ? ' yet.'
          : '.'}
      </Typography>
      {isOverTwoWeeksOld && (
        <Typography
          variant="caption"
          sx={{ mt: 1, textAlign: 'center', maxWidth: '400px' }}
        >
          This sync job is over two weeks old. <br />
          Logs are deleted after two weeks.
          <br />
          Contact{' '}
          <Link href="mailto:support@aquifer.cloud">
            support@aquifer.cloud
          </Link>{' '}
          if you need more assistance.
        </Typography>
      )}
    </div>
  );
}

interface JobLogsTableProps {
  query: string; // Full query sent to backend (includes job filter prefix)
  displayQuery: string; // User-visible portion shown in the input
  defaultQuery: string;
  onQueryChange: (query: string) => void;
  queryMode: 'basic' | 'advanced';
  onQueryModeChange: (mode: 'basic' | 'advanced') => void;
  startTime?: string;
  endTime?: string;
  onTimeChange: (range: { start: string; end: string }) => void;
  currentAccount: any;
  timezone: string;
  sync: any;
  jobFilterPrefix?: string;
}

const JobLogsTable: React.FC<JobLogsTableProps> = ({
  query,
  displayQuery,
  defaultQuery,
  onQueryChange,
  queryMode,
  onQueryModeChange,
  startTime,
  endTime,
  onTimeChange,
  currentAccount,
  timezone,
  sync,
  jobFilterPrefix,
}) => {
  const { palette } = useTheme();
  const levelColorMap: Record<string, string> = {
    error: palette.error.dark,
    warning: palette.warning.light,
    warn: palette.warning.light,
    info: palette.info.main,
    debug: palette.grey[400],
  };

  // Separate draft per mode so switching tabs preserves in-progress text
  const [basicDraft, setBasicDraft] = React.useState('');
  const [advancedDraft, setAdvancedDraft] = React.useState(defaultQuery);
  const draftQuery = queryMode === 'basic' ? basicDraft : advancedDraft;
  const setDraftQuery =
    queryMode === 'basic' ? setBasicDraft : setAdvancedDraft;

  const [submittedQuery, setSubmittedQuery] = React.useState(query);
  const [queryError, setQueryError] = React.useState<string | null>(null);
  const [surroundingTarget, setSurroundingTarget] = React.useState<{
    logStream: string;
    timestamp: number;
    jobIds: string;
  } | null>(null);
  const [expandedDetailRowIds, setExpandedDetailRowIds] = React.useState<any[]>(
    [],
  );

  // Sync submitted query when the full query changes externally (e.g., jobFilterPrefix changed)
  React.useEffect(() => {
    setSubmittedQuery(query);
  }, [query]);

  const logQuery = useLogQuery(currentAccount?.id, {
    query: submittedQuery,
    start_time: startTime,
    end_time: endTime,
  });

  // Extract error message from 400 responses
  React.useEffect(() => {
    if (logQuery.error) {
      const detail = (logQuery.error as any)?.response?.data?.detail;
      setQueryError(detail || 'Query failed');
    } else {
      setQueryError(null);
    }
  }, [logQuery.error]);

  const handleSubmit = () => {
    setQueryError(null);
    if (queryMode === 'basic') {
      const fullQuery = [jobFilterPrefix, buildBasicQuery(draftQuery)]
        .filter(Boolean)
        .join(' | ');
      setSubmittedQuery(fullQuery);
      onQueryChange(draftQuery);
    } else {
      const fullQuery = [jobFilterPrefix, draftQuery]
        .filter(Boolean)
        .join(' | ');
      setSubmittedQuery(fullQuery);
      onQueryChange(draftQuery);
    }
  };

  const response = logQuery.data;
  const isStatsQuery = response?.meta?.query_type === 'stats';
  const warnings = response?.meta?.warnings || [];

  // Transform response data into grid rows
  const rows = React.useMemo(() => {
    if (!response?.data) return [];
    if (isStatsQuery) {
      return response.data.map((row: any, i: number) => ({ id: i, ...row }));
    }
    // Log events: extract from JSON:API envelope
    return response.data.map((item: any) => ({
      id: item.id,
      ...item.attributes,
    }));
  }, [response, isStatsQuery]);

  // Collect unique job IDs from loaded rows for surrounding logs filtering
  const allJobIds = React.useMemo(() => {
    const ids = new Set<string>();
    for (const row of rows) {
      if (row.job_id) ids.add(row.job_id);
    }
    return Array.from(ids).join(',');
  }, [rows]);

  // Column definitions for stats queries (dynamic from all rows' keys)
  const statsColumns = React.useMemo(() => {
    if (!isStatsQuery || !rows.length) return [];
    const keySet = new Set<string>();
    for (const row of rows) {
      for (const key of Object.keys(row)) {
        if (key !== 'id') keySet.add(key);
      }
    }
    return Array.from(keySet).map((key) => ({
      field: key,
      headerName: key,
      flex: 1,
      sortable: true,
    }));
  }, [isStatsQuery, rows]);

  // Column definitions for log event queries
  const sortable = queryMode === 'basic';
  const logColumns = React.useMemo(
    () => [
      {
        field: 'timestamp',
        headerName: 'Time',
        sortable,
        filterable: false,
        width: 250,
        renderCell: (params: { value?: string }) => (
          <Tooltip title={(params?.value ?? '') + ' UTC'}>
            <span style={{ color: palette.grey[600] }}>
              {params?.value
                ? formatInTimeZone(
                    parseJSON(params.value),
                    timezone,
                    'MMM dd, yyyy h:mm:ss.SSS a zzz',
                  )
                : ''}
            </span>
          </Tooltip>
        ),
      },
      {
        field: 'level',
        headerName: 'Level',
        width: 100,
        sortable,
        filterable: false,
        renderCell: (params: { value?: string }) => (
          <span
            style={{
              fontWeight: 'bold',
              fontFamily: 'monospace',
              color: levelColorMap[params.value as string],
            }}
          >
            {capitalize(params.value ?? '')}
          </span>
        ),
      },
      {
        field: 'message_txt',
        headerName: 'Message',
        flex: 3,
        sortable,
        filterable: false,
        renderCell: (params: any) => {
          const messageJson = JSON.parse(params.row.message ?? '{}');
          let displayMessage = messageJson?.message;
          if (displayMessage === 'Request' && messageJson?.request) {
            const status = messageJson.response?.status ?? '';
            const method = messageJson.request?.method ?? '';
            const url = messageJson.request?.url ?? '';
            displayMessage = [status, method, url].filter(Boolean).join(' ');
          }
          return (
            <code>
              {displayMessage}
              {messageJson?.error && (
                <span style={{ color: palette.error.dark, marginLeft: '5px' }}>
                  - {messageJson.error}
                </span>
              )}
            </code>
          );
        },
      },
      {
        headerName: 'Log Details',
        flex: 0.5,
        field: GRID_DETAIL_PANEL_TOGGLE_FIELD,
        sortable: false,
        filterable: false,
        renderCell: (params: any) => {
          const isExpanded = expandedDetailRowIds.includes(params.id);
          return (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                color: '#0288d1',
                cursor: 'pointer',
              }}
            >
              <IconCodeDots size={16} />
              <span style={{ marginLeft: '5px' }}>
                {isExpanded ? 'Hide JSON' : 'View JSON'}
              </span>
            </div>
          );
        },
      },
    ],
    [palette, timezone, levelColorMap, sortable, expandedDetailRowIds],
  );

  const meta = response?.meta;
  const recordCount = meta?.count ?? rows.length;

  const handleBasicKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      {/* Mode toggle + time range picker */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 1,
        }}
      >
        <Tabs
          value={queryMode}
          onChange={(_, v) => onQueryModeChange(v)}
          variant="standard"
          sx={{
            minHeight: 0,
            '& .MuiTab-root': {
              minHeight: 36,
              py: 0.5,
              px: 1.5,
              textTransform: 'none',
              fontSize: '13px',
            },
            '& .MuiTabs-indicator': { height: 2 },
          }}
        >
          <Tab value="basic" label="Search" />
          <Tab value="advanced" label="Query" />
        </Tabs>
        {startTime && endTime && (
          <AQDatePicker
            start={startTime}
            end={endTime}
            onTimeChange={onTimeChange}
            hideTimeZoneSelector={true}
            sx={{
              container: {
                display: 'block',
                maxWidth: '600px',
                marginRight: 0,
              },
            }}
          />
        )}
      </Box>

      {/* Conditional toolbar */}
      {queryMode === 'basic' ? (
        <Box sx={{ mb: 1 }}>
          <TextField
            value={draftQuery}
            onChange={(e) => setDraftQuery(e.target.value)}
            onKeyDown={handleBasicKeyDown}
            placeholder="Search log messages..."
            fullWidth
            size="small"
            autoComplete="off"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <IconSearch size={18} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  {draftQuery && (
                    <IconButton onClick={() => setDraftQuery('')} size="small">
                      <IconX size={16} />
                    </IconButton>
                  )}
                  <IconButton
                    onClick={handleSubmit}
                    disabled={logQuery.isLoading}
                    size="small"
                  >
                    {logQuery.isLoading ? (
                      <CircularProgress size={18} />
                    ) : (
                      <IconPlayerPlay size={18} />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            error={Boolean(queryError)}
            helperText={queryError}
          />
        </Box>
      ) : (
        <LogQueryToolbar
          query={draftQuery}
          onChange={setDraftQuery}
          onSubmit={handleSubmit}
          error={queryError}
          isLoading={logQuery.isLoading}
        />
      )}

      {/* Warnings */}
      {warnings.length > 0 && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          {warnings.map((w: string, i: number) => (
            <div key={i}>{w}</div>
          ))}
        </Alert>
      )}

      {/* Records limited banner */}
      {!isStatsQuery && recordCount > rows.length && (
        <Alert severity="info" sx={{ mb: 1 }}>
          Showing {rows.length.toLocaleString()} of{' '}
          {recordCount.toLocaleString()} matching logs. Narrow your time range
          or add filters to see more specific results.
        </Alert>
      )}

      {/* Data grid */}
      <Box
        id="aq-datagrid--logs"
        sx={{ width: '100%', maxWidth: '100%', position: 'relative' }}
      >
        {logQuery.isError && !queryError ? (
          <Box sx={{ maxWidth: '600px', margin: '24px auto' }}>
            <CustomNoRowsOverlay sync={sync} />
          </Box>
        ) : (
          <DataGridPremium
            rows={rows}
            columns={isStatsQuery ? statsColumns : logColumns}
            loading={logQuery.isLoading}
            pagination={false}
            disableAggregation
            disableColumnFilter
            sortingMode="client"
            density="compact"
            autoHeight
            detailPanelExpandedRowIds={expandedDetailRowIds}
            onDetailPanelExpandedRowIdsChange={setExpandedDetailRowIds}
            getRowClassName={(params) =>
              isStatsQuery
                ? ''
                : `aq-datagrid-row aq-datagrid-row--${(params.row.level ?? 'info').toLowerCase()}`
            }
            {...(!isStatsQuery && {
              getDetailPanelContent: ({ row }: { row: any }) => {
                const parsed = JSON.parse(row.message ?? '{}');
                const logStream =
                  row.logStream || row['@logStream'] || parsed['@logStream'];
                return (
                  <Box>
                    <Box
                      sx={{ p: 1, display: 'flex', justifyContent: 'flex-end' }}
                    >
                      <Tooltip
                        title={
                          logStream
                            ? 'View logs around this event'
                            : 'No log stream available for this event'
                        }
                      >
                        <span>
                          <Button
                            size="small"
                            startIcon={<IconArrowsVertical size={16} />}
                            disabled={!logStream}
                            onClick={() => {
                              setSurroundingTarget({
                                logStream,
                                timestamp: new Date(
                                  row.timestamp + ' UTC',
                                ).getTime(),
                                jobIds: allJobIds,
                              });
                            }}
                          >
                            View Surrounding Logs
                          </Button>
                        </span>
                      </Tooltip>
                    </Box>
                    <ReactJson
                      style={{
                        padding: '10px',
                        margin: '0 14px',
                        cursor: 'pointer',
                      }}
                      name={false}
                      displayObjectSize={false}
                      displayDataTypes={false}
                      collapsed={false}
                      src={parsed}
                    />
                  </Box>
                );
              },
              getDetailPanelHeight: () => 'auto' as const,
            })}
            slots={{
              noRowsOverlay: () => <CustomNoRowsOverlay sync={sync} />,
            }}
            slotProps={{
              loadingOverlay: {
                variant: 'skeleton' as const,
                noRowsVariant: 'skeleton' as const,
              },
            }}
            sx={{
              boxShadow: '5px 0 5px rgba(173, 216, 230, 0.25)',
              '& .MuiDataGrid-columnHeaderTitle': {
                fontWeight: 'bold',
              },
            }}
          />
        )}
      </Box>

      {surroundingTarget && (
        <SurroundingLogsDialog
          open={Boolean(surroundingTarget)}
          onClose={() => setSurroundingTarget(null)}
          accountId={currentAccount?.id}
          logStream={surroundingTarget.logStream}
          anchorTimestamp={surroundingTarget.timestamp}
          timezone={timezone}
          jobIds={surroundingTarget.jobIds}
        />
      )}
    </Box>
  );
};

export default JobLogsTable;
